import { Zap, Flame, Target } from "lucide-react";
import { cn } from "@/lib/utils";

interface ProgressStripProps {
  repsWeek: number | null;
  streakDays: number | null;
  arcCompletion: number | null;
  arcTitle: string | null;
}

function Chip({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <div
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground"
    >
      {children}
    </div>
  );
}

export function ProgressStrip({ repsWeek, streakDays, arcCompletion, arcTitle }: ProgressStripProps) {
  const allEmpty =
    (repsWeek === null || repsWeek === 0) &&
    (streakDays === null || streakDays === 0) &&
    (arcCompletion === null || arcTitle === null);

  if (allEmpty) {
    return (
      <div className="flex flex-wrap gap-2" data-testid="progress-strip">
        <Chip testId="chip-empty">
          <Zap className="h-3.5 w-3.5" />
          <span>Complete your first session to see stats</span>
        </Chip>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid="progress-strip">
      <Chip testId="chip-reps">
        <Zap className="h-3.5 w-3.5 text-yellow-500" />
        <span>
          {repsWeek && repsWeek > 0 ? (
            <>{repsWeek} {repsWeek === 1 ? "rep" : "reps"} this week</>
          ) : (
            "First rep coming up"
          )}
        </span>
      </Chip>

      <Chip testId="chip-streak">
        <Flame className={cn("h-3.5 w-3.5", streakDays && streakDays > 0 ? "text-orange-500" : "text-muted-foreground")} />
        <span>
          {streakDays && streakDays > 0 ? (
            <>{streakDays}-day streak</>
          ) : (
            "Getting started"
          )}
        </span>
      </Chip>

      {arcCompletion !== null && arcTitle && (
        <Chip testId="chip-arc">
          <Target className="h-3.5 w-3.5 text-blue-500" />
          <span>
            {arcCompletion}% through &ldquo;{arcTitle}&rdquo;
          </span>
        </Chip>
      )}
    </div>
  );
}
